import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GraduationCap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(form.name, form.email, form.password, form.code.trim());
      toast.success("Account created!");
      const cr = data.code_result;
      if (cr) {
        if (cr.status === "invalid") toast.error(cr.message);
        else toast.success(cr.message);
        if (cr.already_assigned_to) toast.info(`This code has already been assigned to ${cr.already_assigned_to}`);
      }
      navigate(cr && cr.status === "free_access" ? "/dashboard" : "/intake");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A192F] p-12 text-white">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><GraduationCap className="h-5 w-5 text-amber-500" /></div>
          <span className="font-heading font-bold">Insurance License Lab</span>
        </Link>
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Start free.<br /><span className="text-amber-500">No card required.</span></h1>
          <ul className="mt-6 space-y-3 text-slate-300">
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Sample workbook &amp; audio</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> 10 practice questions daily</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> $49.95/year — cancel anytime</li>
          </ul>
        </div>
        <p className="text-xs text-slate-500 font-mono">CALIFORNIA · PROPERTY &amp; CASUALTY</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#0A192F] flex items-center justify-center"><GraduationCap className="h-5 w-5 text-amber-500" /></div>
            <span className="font-heading font-bold text-[#0A192F]">Insurance License Lab</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#0A192F]">Create your free account</h2>
          <p className="text-sm text-slate-500 mt-1">Get instant access to sample content</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="register-password-input" />
              <p className="text-xs text-slate-400 mt-1">At least 6 characters</p>
            </div>
            <div>
              <Label htmlFor="code">Discount or access code <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1.5 font-mono uppercase" placeholder="ENTER CODE" data-testid="register-code-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11" data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create free account"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-amber-600 font-semibold">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
